// SegmentCard.tsx — object/background separation. Auto runs the server's
// plane + connected-component segmentation; Box keeps exactly the gaussians
// inside a world box (entered in mm, sent in metres; the fields hold raw
// strings so negatives and cleared fields can be typed, see boxFields.ts, and
// Segment is disabled while any is not a number); Guided lets the user name
// several segments and seed each by tapping the model
// (GuidedSegmentsPanel, useGuidedSegments), and every segment exports as its
// own splat. Either way the page reloads the splats so the new labels show,
// and the dimensions update.
import { useEffect, useMemo, useRef, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import { BsBoundingBox } from 'react-icons/bs';
import { ui } from 'common';
import { api } from '../api';
import type { DimensionsResult, Scan, SegmentResult } from '../api';
import { errorMessage } from '../format';
import { LABEL_BACKGROUND, LABEL_OBJECT, LABEL_PLANE, cloudBounds } from '../splat/format';
import type { SplatCloud } from '../splat/format';
import { EMPTY_BOX_FIELDS, boxFieldsFromMetres, parseBoxFields, setBoxField } from './boxFields';
import type { BoxFields } from './boxFields';
import { GuidedSegmentsPanel } from './GuidedSegmentsPanel';
import type { GuidedSegments } from './useGuidedSegments';

const { Dropdown } = ui;
type Mode = 'auto' | 'box' | 'guided';
const AXIS_NAMES = ['X', 'Y', 'Z'];

export interface SegmentCardProps {
  scan: Scan;
  cloud: SplatCloud | null;
  onSegmented: (dims: DimensionsResult | null) => void;
  onError: (msg: string) => void;
  guided: GuidedSegments;
}

function labelCounts(c: SplatCloud | null): { object: number; background: number; plane: number } {
  const n = { object: 0, background: 0, plane: 0 };
  if (!c) return n;
  for (let i = 0; i < c.count; i++) {
    if (c.label[i] === LABEL_OBJECT) n.object++;
    else if (c.label[i] === LABEL_BACKGROUND) n.background++;
    else if (c.label[i] === LABEL_PLANE) n.plane++;
  }
  return n;
}

export function SegmentCard({ scan, cloud, onSegmented, onError, guided }: SegmentCardProps) {
  // A scan with a stored guide opens in Guided mode, so its segments show.
  const [mode, setMode] = useState<Mode>(scan.guide ? 'guided' : 'auto');
  const setGuidedActive = guided.setActive;
  useEffect(() => {
    setGuidedActive(mode === 'guided');
  }, [mode, setGuidedActive]);
  useEffect(() => () => setGuidedActive(false), [setGuidedActive]);
  const [box, setBox] = useState<BoxFields>(EMPTY_BOX_FIELDS);
  const parsedBox = useMemo(() => parseBoxFields(box), [box]);
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState<SegmentResult | null>(null);
  const counts = useMemo(() => labelCounts(cloud), [cloud]);

  // Seed the box editor from the current object (or everything) in mm.
  // Keyed on scan + count so a live delta that only nudges positions doesn't
  // overwrite a box the user is editing.
  const cloudRef = useRef(cloud);
  cloudRef.current = cloud;
  const boundsKey = cloud ? `${scan.id}:${cloud.count}` : '';
  useEffect(() => {
    const c = cloudRef.current;
    if (!c || !boundsKey) return;
    const b = cloudBounds(c, [LABEL_OBJECT]) ?? cloudBounds(c, []);
    if (b) setBox(boxFieldsFromMetres(b.min, b.max));
  }, [boundsKey]);

  const run = async () => {
    if (mode === 'guided') {
      await guided.apply();
      return;
    }
    if (mode === 'box' && !parsedBox) return;
    setBusy(true);
    try {
      const r =
        mode === 'auto' || !parsedBox
          ? await api.segment(scan.id, { mode: 'auto' })
          : await api.segment(scan.id, { mode: 'box', box: parsedBox });
      console.info('[openscan/viewer] segmentation done', r.stats);
      setLast(r);
      onSegmented(r.dimensions);
    } catch (err) {
      onError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const setEdge = (edge: 'min' | 'max', k: number, v: string) => setBox((prev) => setBoxField(prev, edge, k, v));
  const boxInvalid = mode === 'box' && !parsedBox;

  const stats = last?.stats;
  return (
    <Card>
      <Card.Header>Segmentation</Card.Header>
      <Card.Body>
        <div className="small mb-2">
          object <span className="measure-value">{stats?.object ?? counts.object}</span> · background {stats?.background ?? counts.background} · plane{' '}
          {stats?.plane ?? counts.plane}
        </div>
        {mode === 'box' && (
          <div className="d-flex flex-column gap-1">
            {AXIS_NAMES.map((name, k) => (
              <div key={name} className="d-flex align-items-center gap-2 small">
                <span style={{ minWidth: '1.5rem' }}>{name}</span>
                <Form.Control type="number" size="sm" value={box.min[k]} onChange={(e) => setEdge('min', k, e.target.value)} aria-label={`${name} min mm`} />
                <span>to</span>
                <Form.Control type="number" size="sm" value={box.max[k]} onChange={(e) => setEdge('max', k, e.target.value)} aria-label={`${name} max mm`} />
                <span>mm</span>
              </div>
            ))}
          </div>
        )}
        {boxInvalid && <div className="small text-danger mt-1">Every edge needs a number, with min ≤ max.</div>}
        {mode === 'guided' && <GuidedSegmentsPanel g={guided} disabled={busy || guided.busy || scan.status === 'refining'} />}
      </Card.Body>
      <Card.Footer className="d-flex align-items-center gap-2">
        <Dropdown
          value={mode}
          options={[
            { value: 'auto', label: 'Auto' },
            { value: 'box', label: 'Box' },
            { value: 'guided', label: 'Guided' },
          ]}
          onChange={(e) => setMode(e.target.value as Mode)}
          aria-label="Segmentation mode"
        />
        <Button
          size="sm"
          variant="outline-primary"
          disabled={busy || !scan.has_splats || boxInvalid || (mode === 'guided' && (guided.busy || !!guided.problem || scan.status === 'refining'))}
          onClick={() => void run()}
        >
          <BsBoundingBox className="me-1" />
          {busy || guided.busy ? 'Segmenting…' : mode === 'guided' ? 'Apply segments' : 'Segment'}
        </Button>
      </Card.Footer>
    </Card>
  );
}
